import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ErrorOutline } from "@mui/icons-material";
import useDashboardCalls, {
  METRICS_LOOKBACK_DAYS,
} from "../../hooks/useDashboardCalls";
import CallMetricCards from "./CallMetricCards";
import CallVolumeModal from "./CallVolumeModal";
import CallsListPanel from "./CallsListPanel";
import LoadingAnimation from "../reusables/LoadingAnimation";
import WhatNextModal from "../modals/WhatNextModal";
import { captureAnalyticsEvent } from "../../utils/posthogAnalytics";

const SHOW_WHAT_NEXT_KEY = "showWhatNextModal";
const PROMPT_INBOUND_ASSISTANT_KEY = "promptInboundAssistant";
/** Prevents Strict Mode double-capture of the "shown" event in a single open cycle. */
const WHAT_NEXT_SHOWN_LOGGED_KEY = "whatNextModalShownLogged";

function readWhatNextFlag() {
  try {
    return localStorage.getItem(SHOW_WHAT_NEXT_KEY) === "true";
  } catch {
    return false;
  }
}

function clearWhatNextFlag() {
  try {
    localStorage.removeItem(SHOW_WHAT_NEXT_KEY);
  } catch {
    /* ignore */
  }
}

function clearWhatNextShownLogged() {
  try {
    sessionStorage.removeItem(WHAT_NEXT_SHOWN_LOGGED_KEY);
  } catch {
    /* ignore */
  }
}

const Home = () => {
  const navigate = useNavigate();
  const {
    calls,
    metricCalls,
    metricsLoader,
    metricsDateRange,
    loader,
    errMsg,
    dateRange,
    setDateRange,
    callDateRange,
    handleDateRangeChange,
  } = useDashboardCalls(30);

  const [chartOpen, setChartOpen] = useState(false);
  const [chartSeries, setChartSeries] = useState("total");
  // Lazy-read so Strict Mode remounts still open the modal (don't clear until dismiss/choice).
  const [whatNextOpen, setWhatNextOpen] = useState(readWhatNextFlag);

  useEffect(() => {
    if (!whatNextOpen) return;
    try {
      if (sessionStorage.getItem(WHAT_NEXT_SHOWN_LOGGED_KEY) === "1") return;
      sessionStorage.setItem(WHAT_NEXT_SHOWN_LOGGED_KEY, "1");
    } catch {
      /* still capture if sessionStorage is unavailable */
    }
    captureAnalyticsEvent("What next modal shown");
  }, [whatNextOpen]);

  const openChart = (seriesId) => {
    setChartSeries(seriesId || "total");
    setChartOpen(true);
  };

  const closeWhatNext = () => {
    clearWhatNextFlag();
    clearWhatNextShownLogged();
    captureAnalyticsEvent("What next modal dismissed");
    setWhatNextOpen(false);
  };

  const handleChooseInbound = () => {
    clearWhatNextFlag();
    clearWhatNextShownLogged();
    captureAnalyticsEvent("What next modal choice selected", {
      choice: "inbound",
    });
    try {
      localStorage.setItem(PROMPT_INBOUND_ASSISTANT_KEY, "true");
    } catch {
      /* ignore */
    }
    setWhatNextOpen(false);
    navigate("/dashboard/create-route", {
      state: { promptInboundAssistant: true },
    });
  };

  const handleChooseOutbound = () => {
    clearWhatNextFlag();
    clearWhatNextShownLogged();
    captureAnalyticsEvent("What next modal choice selected", {
      choice: "outbound",
    });
    setWhatNextOpen(false);
    navigate("/dashboard/dialer");
  };

  return (
    <div className="pt-4 px-4 pb-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="lg:text-[22px] text-[20px] font-bold text-gray-900 lg:mt-0 mt-8">
          Dashboard
        </h1>
        {(loader || metricsLoader) && (
          <LoadingAnimation
            style={{ width: 28, height: 28 }}
            alt="Loading"
          />
        )}
      </div>

      {errMsg && (
        <div className="text-center text-red-500 lg:text-[13px] text-[12px] mt-2 font-semibold mb-3">
          <ErrorOutline sx={{ fontSize: "" }} /> {errMsg}
        </div>
      )}

      <CallMetricCards
        calls={metricCalls}
        isLoading={metricsLoader}
        onOpenChart={openChart}
      />

      {/* Chart from KPI cards uses the fixed metrics window, not the table filter */}
      <CallVolumeModal
        open={chartOpen}
        onClose={() => setChartOpen(false)}
        calls={metricCalls}
        dateRange={METRICS_LOOKBACK_DAYS}
        startDate={metricsDateRange.startDate}
        endDate={metricsDateRange.endDate}
        isLoading={metricsLoader}
        initialSeries={chartSeries}
      />

      <CallsListPanel
        calls={calls}
        navigate={navigate}
        dateRange={dateRange}
        onDatePresetChange={setDateRange}
        startDate={callDateRange.startDate}
        endDate={callDateRange.endDate}
        onCustomDateChange={handleDateRangeChange}
        isLoading={loader}
      />

      {!loader && calls.length === 0 && (
        <p className="mt-3 text-sm text-gray-500 px-1">
          You don&apos;t have any calls in this range yet. When calls come in,
          they&apos;ll show up here.
        </p>
      )}

      <WhatNextModal
        open={whatNextOpen}
        onClose={closeWhatNext}
        onChooseInbound={handleChooseInbound}
        onChooseOutbound={handleChooseOutbound}
      />
    </div>
  );
};

export default Home;
